import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "connection-modal__header-inner" }
const _hoisted_2 = {
  key: 0,
  class: "connection-modal__subtitle"
}
const _hoisted_3 = { class: "connection-modal__body" }
const _hoisted_4 = { class: "connection-modal__form-group" }
const _hoisted_5 = { class: "connection-modal__ai-row" }
const _hoisted_6 = { class: "connection-modal__ai-buttons" }
const _hoisted_7 = ["disabled", "title"]
const _hoisted_8 = {
  key: 0,
  class: "connection-modal__error",
  role: "alert"
}
const _hoisted_9 = { class: "connection-modal__actions" }

import { Icon } from "@iconify/vue";
import { ref, watch } from "vue";
import { Button, Modal, Textarea } from "@/components/ui";
import AiRefinePopover from "@/components/socials/AiRefinePopover.vue";
import AIGenerationService from "@/services/ai/ai-generation";
import { useToast } from "@/composables/ui/useToast";

interface Props {
  show: boolean;
  isLoading?: boolean;
  error?: string | null;
  contactName?: string | null;
  initialMessage?: string;
  contactId?: string;
  icpId?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'LinkedInConnectionRequestModal',
  props: {
    show: { type: Boolean },
    isLoading: { type: Boolean, default: false },
    error: { default: null },
    contactName: { default: null },
    initialMessage: { default: "" },
    contactId: {},
    icpId: {}
  },
  emits: ["close", "send"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const noteText = ref("");
const showAiPanel = ref(false);
const isGeneratingAiCustom = ref(false);
const aiRefineButtonRef = ref<HTMLElement | null>(null);
const { success, error: toastError } = useToast();

const handleAiCustomizationGenerate = async (payload: { customInstructions: string }) => {
  if (isGeneratingAiCustom.value) return;
  isGeneratingAiCustom.value = true;
  try {
    const response = await AIGenerationService.generateRefine({
      contactId: props.contactId || "",
      icpId: props.icpId,
      channel: "linkedin",
      currentDraft: noteText.value,
      customInstructions:
        `${payload.customInstructions || ""} (Keep the message under 200 characters for a LinkedIn connection note)`.trim(),
    });

    const refinedText = response.body || "";
    // Still slice just in case the AI ignores the instruction
    noteText.value = refinedText.length > 200 ? refinedText.slice(0, 200) : refinedText;
    showAiPanel.value = false;
    success("Message refined");
  } catch (err) {
    console.error("Failed to refine LinkedIn message:", err);
    toastError("Failed to refine message");
  } finally {
    isGeneratingAiCustom.value = false;
  }
};

watch(
  () => props.show,
  (isShowing) => {
    if (isShowing) {
      noteText.value = (props.initialMessage || "").slice(0, 200);
      showAiPanel.value = false;
    }
  }
);

const handleSend = () => {
  if (noteText.value.length > 200) {
    toastError("Message exceeds 200 character limit");
    return;
  }
  emit("send", {
    message: noteText.value.trim() || undefined,
  });
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Modal), {
    show: __props.show,
    title: "Add a note to your invitation",
    size: "sm",
    "dialog-max-width": '500px',
    "backdrop-opacity": 0.5,
    closeOnBackdrop: false,
    zIndex: 10060,
    onClose: _cache[4] || (_cache[4] = ($event: any) => (emit('close')))
  }, {
    header: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _cache[5] || (_cache[5] = _createElementVNode("h2", {
          id: "modal-title",
          class: "connection-modal__title"
        }, "Add a note to your invitation", -1)),
        (__props.contactName)
          ? (_openBlock(), _createElementBlock("p", _hoisted_2, " Sending invitation to " + _toDisplayString(__props.contactName), 1))
          : _createCommentVNode("", true)
      ])
    ]),
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_9, [
        _createVNode(_unref(Button), {
          variant: "secondary",
          onClick: _cache[3] || (_cache[3] = ($event: any) => (emit('close'))),
          disabled: __props.isLoading
        }, {
          default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
            _createTextVNode(" Cancel ", -1)
          ]))]),
          _: 1
        }, 8, ["disabled"]),
        _createVNode(_unref(Button), {
          variant: "primary",
          loading: __props.isLoading,
          disabled: __props.isLoading,
          onClick: handleSend
        }, {
          default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
            _createTextVNode(" Send Invitation ", -1)
          ]))]),
          _: 1
        }, 8, ["loading", "disabled"])
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_3, [
        _cache[7] || (_cache[7] = _createElementVNode("p", { class: "connection-modal__guidance" }, " LinkedIn members are more likely to accept invitations that include a personal note. ", -1)),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_unref(Textarea), {
            id: "connection-message",
            modelValue: noteText.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((noteText).value = $event)),
            label: "Personalized Message (Optional)",
            placeholder: "Ex: We know each other from...",
            "max-length": 200,
            "show-char-count": false,
            disabled: __props.isLoading,
            rows: 5,
            resize: 'vertical',
            class: "connection-modal__textarea"
          }, null, 8, ["modelValue", "disabled"]),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("button", {
                ref_key: "aiRefineButtonRef",
                ref: aiRefineButtonRef,
                type: "button",
                class: "connection-modal__ai-btn connection-modal__ai-btn--refine",
                disabled: isGeneratingAiCustom.value,
                title: isGeneratingAiCustom.value ? 'Generating...' : 'AI Refine',
                onClick: _cache[1] || (_cache[1] = ($event: any) => (showAiPanel.value = !showAiPanel.value))
              }, [
                _createVNode(_unref(Icon), {
                  icon: "mdi:sparkles",
                  class: "connection-modal__ai-btn-icon"
                }),
                _cache[6] || (_cache[6] = _createElementVNode("span", { class: "connection-modal__ai-btn-text" }, "AI Refine", -1))
              ], 8, _hoisted_7)
            ]),
            _createElementVNode("span", {
              class: _normalizeClass(["connection-modal__char-count", { 'connection-modal__char-count--error': noteText.value.length > 200 }])
            }, _toDisplayString(noteText.value.length) + "/200 ", 3)
          ]),
          _createVNode(AiRefinePopover, {
            show: showAiPanel.value,
            "trigger-el": aiRefineButtonRef.value,
            disabled: isGeneratingAiCustom.value,
            onGenerate: handleAiCustomizationGenerate,
            onClose: _cache[2] || (_cache[2] = ($event: any) => (showAiPanel.value = false))
          }, null, 8, ["show", "trigger-el", "disabled"])
        ]),
        (__props.error)
          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
              _createVNode(_unref(Icon), {
                icon: "mdi:alert-circle",
                class: "connection-modal__error-icon",
                "aria-hidden": "true"
              }),
              _createElementVNode("span", null, _toDisplayString(__props.error), 1)
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }, 8, ["show"]))
}
}

})